import {Component, FontAwesomeIcon, DocumentChange} from 'substance'
import moment from 'moment'
import {Event} from '../../../lib/constants/Event'

/**
 * @typedef UIDatetimeFieldEditor.props
 * @property {string|string[]} field - Name of the node field to edit
 * @property {Node} node - The node
 * @property {string} label - Label of the date editor
 * @property {string} [type=datetime] - one of 'datetime', 'date', 'time'
 * @property {string} [icon] - An optional icon, defa
 * @property {Boolean} [disabled=false] - If the input should be disabled
 */

/**
 * @typedef UIDatetimeFieldEditor.state
 * @property {boolean} editing - If editing
 */

/**
 * An editor component that lets the user edit text fields on a node.
 *
 * @name UIDatetimeFieldEditor
 * @class
 *
 * @see {@link UIFieldEditor} for text editing
 *
 * @example
 * import {UIDateTimeFieldEditor} from 'writer'
 *
 * render($$) {
 *    const el = $$('div')
 *    return el.append(
 *        $$(UIDateTimeFieldEditor, {
 *            node: this.props.node,
 *            field: 'title',
 *            placeholder: this.getLabel('Add title here')
 *        })
 *    )
 * }
 *
 * @param {UIDatetimeFieldEditor.props} props
 * @property {UIDatetimeFieldEditor.props} props
 * @property {UIDatetimeFieldEditor.state} state
 */
class DatetimeFieldEditorComponent extends Component {

    _setDefaultProps() {
        const defaults = {
            type: 'datetime',
            disabled: false,
            icon: this.props.type !== 'time' ? 'fa-calendar' : 'fa-clock-o'
        }
        this.extendProps(Object.assign({}, defaults, this.props))
    }

    getPath() {
        return this.fieldPath
    }

    get datetime() {
        let date = null
        let time = null
        if (this.fieldValue) {
            let datetime = moment(this.fieldValue, this.datetimeFormat)
            date = datetime.format('YYYY-MM-DD')
            time = datetime.format('HH:mm')
        }
        return {date, time}
    }

    get fieldValue() {
        return this.props.node.document.get(this.fieldPath)
    }

    set fieldValue(val) {
        const original = this.fieldValue
        if (original !== val) {
            this.props.node.document.set(this.fieldPath, val)
            this._triggerDocumentChanged(original, val)
        }
    }

    get fieldPath() {
        const field = this.props.field
        const node = this.props.node
        return Array.isArray(field) ? [node.id, ...field] : [node.id, field]
    }

    get datetimeFormat() {
        if (this.props.type === 'time') {
            return 'HH:mm:ss'
        } else if (this.props.type === 'date') {
            return 'YYYY-MM-DD'
        } else {
            return 'YYYY-MM-DDTHH:mm:ssZ'
        }
    }

    shouldRerender(newProps) {
        // Only rerender when the component's node.id changes, or the component is disabled/enabled
        return this.props.node.id !== newProps.node.id || this.props.disabled !== newProps.disabled
    }

    render($$) {
        this._setDefaultProps()

        if (!this.props.field || !this.props.node || !this.props.label) {
            console.warn('Missing props, field, label or node')
            return $$('span')
        }

        const fieldContainer = $$('div', {
            class: 'im-blocknode__content full-width im-datetime-field-editor'
        }).ref('field-container')

        const labelFor = this.props.type === 'time' ? 'timeEditor' : 'dateEditor'

        const label = $$('label')
            .attr('for', this.props.node.id + this.props.field + labelFor)
            .append(this.props.label)

        const dateEditor = this._renderEditor($$, 'date')

        const timeEditor = this._renderEditor($$, 'time')

        const inputContainer = $$('div').addClass('input-container').ref('input-container')
        if (this.props.type === 'time') {
            inputContainer.append(timeEditor)
        } else if (this.props.type === 'date') {
            inputContainer.append(dateEditor)
        } else {
            inputContainer.append([dateEditor, timeEditor])
        }

        const icon = $$(FontAwesomeIcon, {icon: this.props.icon})
        fieldContainer.append([icon, label, inputContainer])

        return fieldContainer
    }

    _renderEditor($$, editorType) {
        const editor = $$('input')
            .attr('type', editorType)
            .attr('id', this.props.node.id + this.props.field + editorType + 'Editor')
            .addClass('form-control')
            .ref(`${editorType}-editor`)
            .val(this.datetime[editorType])
            .on('change', this._setField)

        if (this.props.disabled) {
            editor.attr('disabled', 'disabled')
        }

        return editor
    }

    _triggerDocumentChanged(original, val) {
        const {api} = this.context

        const op = {
            type: 'set',
            original: original,
            val: val,
            path: this.fieldPath
        }

        const documentChange = new DocumentChange([op], null, null)
        documentChange.updated = {}
        documentChange.updated[this.fieldPath[0]] = true
        documentChange.updated[this.fieldPath.join(',')] = true

        api.events.trigger(null, Event.DOCUMENT_CHANGED, documentChange)
    }

    _setField() {
        const dateRef = this.refs['date-editor']
        const timeRef = this.refs['time-editor']
        const date = dateRef && dateRef.val() || ''
        const time = timeRef && timeRef.val() || ''

        let datetime = moment(null)
        if (this.props.type === 'time' && time !== '') {
            datetime = moment(time, this.datetimeFormat)
        } else if (date !== '' && time !== '') {
            datetime = moment(date + 'T' + time)
        } else if (date !== '' && time === '') {
            datetime = moment(date + 'T00:00')
        }

        if (!datetime.isValid()) {
            this.fieldValue = null
        } else {
            this.fieldValue = datetime.format(this.datetimeFormat)
        }

        this.rerender()
    }
}

export {DatetimeFieldEditorComponent}
